import 'twin.macro';
import React, { useEffect } from 'react';
import { getFicheDePrep } from '@teetsh/app/src/queries/fdp/useFicheDePrep';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { PlusIcon } from '@heroicons/react/20/solid';
import {
  useCloseFicheDePrepDrawer,
  useIsFicheDePrepDrawerOpen,
  useOpenFicheDePrepDrawer,
  useSetFicheDePrepPayload,
} from 'containers/CahierJournal/LinkFichesDePrepDrawer/atoms';
import { useSelectFicheDePrep } from './useSelectFicheDePrep';
import { getSequence } from 'queries/sequences/useSequence';
import { useTranslation } from 'react-i18next';
import { getStandaloneFicheDePrep } from 'queries/fdp/useStandaloneFicheDePrep';
import {
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import EditableTextComponent from 'components/Form/EditableText';
import ButtonExternal from 'components/Buttons/ExternalLink';

interface LinkedFicheDePrep {
  id: string;
  sequenceId?: string;
  label: string;
}

interface Props {
  linkedFichesDePrep?: LinkedFicheDePrep[];
  onChange: (v: LinkedFicheDePrep[]) => void;
  allowAddAnother: boolean;
}

function LinkedFicheDePrepComponent({
  label,
  path,
  onChange,
  onDelete,
}: {
  label: string;
  path: string;
  onChange: (v: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(['common']);
  return (
    <div tw="flex items-center gap-x-2">
      <div tw="px-4 py-3 flex-1 rounded-lg bg-neutral-quiet overflow-x-auto">
        <EditableTextComponent
          tw="mr-4 truncate text-brand-moderate text-base font-semibold"
          value={label}
          size="sm"
          placeholder={t('common:noTitle')}
          onConfirm={onChange}
        />
      </div>
      <ButtonExternal
        minimal
        intent="none"
        href={path}
        icon={ArrowTopRightOnSquareIcon}
      />
      <Button minimal intent="danger" onClick={onDelete} icon={TrashIcon} />
    </div>
  );
}

export function LinkedFichesDePrep(props: Props) {
  const { linkedFichesDePrep = [], onChange, allowAddAnother } = props;
  const onSelectFicheDePrep = useSelectFicheDePrep();
  const setFicheDePrepPayload = useSetFicheDePrepPayload();
  const openDrawer = useOpenFicheDePrepDrawer();
  const isDrawerOpen = useIsFicheDePrepDrawerOpen();
  const closeFicheDePrepDrawer = useCloseFicheDePrepDrawer();
  const { t } = useTranslation(['cahierJournal']);

  useEffect(() => {
    setFicheDePrepPayload({
      selectableType: 'fdp',
      callback: ficheDePrep => {
        onChange([
          ...linkedFichesDePrep,
          {
            id: ficheDePrep.id,
            label: ficheDePrep.title,
            sequenceId: ficheDePrep.sequenceId,
          },
        ]);
        closeFicheDePrepDrawer();
      },
      onSelectFicheDePrep: ({ ficheDePrepId, sequenceId, doNotPrefill }) => {
        if (sequenceId) {
          return getFicheDePrep(sequenceId, ficheDePrepId).then(ficheDePrep => {
            if (!doNotPrefill) {
              getSequence(sequenceId).then(sequence =>
                onSelectFicheDePrep(ficheDePrep, sequence.title),
              );
            }
            return ficheDePrep;
          });
        }
        return getStandaloneFicheDePrep(ficheDePrepId).then(ficheDePrep => {
          if (!doNotPrefill) {
            onSelectFicheDePrep(ficheDePrep, '');
          }
          return ficheDePrep;
        });
      },
      isOpen: isDrawerOpen,
    });
  }, [onChange, linkedFichesDePrep]);

  if (linkedFichesDePrep.length === 0) {
    return null;
  }

  return (
    <div data-testid="linked-fiches-de-prep">
      <h4 tw="text-neutral-moderate mb-2 flex items-center gap-1">
        <DocumentTextIcon tw="inline-block h-5 w-5" />
        {t('cahierJournal:seance.modal.fichesDePrep')}
      </h4>
      <div tw="flex flex-col gap-y-2">
        {linkedFichesDePrep.map(item => (
          <LinkedFicheDePrepComponent
            key={item.id}
            label={item.label}
            path={`/fiches-de-prep/${item.id}`}
            onChange={v =>
              onChange(
                linkedFichesDePrep.map(f =>
                  f.id === item.id ? { ...f, label: v } : f,
                ),
              )
            }
            onDelete={() =>
              onChange(linkedFichesDePrep.filter(f => f.id !== item.id))
            }
          />
        ))}
        {(linkedFichesDePrep || []).length > 0 && allowAddAnother && (
          <div>
            <Button small icon={PlusIcon} onClick={() => openDrawer()}>
              {t('cahierJournal:actions.addAnotherFicheDePrep')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
